import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import blogData from '../data/blogs';

const BlogDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState({
    name: '',
    email: '',
    comment: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);
  const [timer, setTimer] = useState(0);
  const [showVerification, setShowVerification] = useState(false);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [highlightContinue, setHighlightContinue] = useState(false);
  const [highlightVerification, setHighlightVerification] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showScrollPrompt, setShowScrollPrompt] = useState(false);
  const [hasScrolledDown, setHasScrolledDown] = useState(false);

  useEffect(() => {
    // Reset all states when page/slug changes
    setLoading(true);
    setTimer(0);
    setShowVerification(false);
    setIsTimerRunning(false);
    setHighlightContinue(false);
    setHighlightVerification(false);
    setIsLoading(false);
    setOpenFAQ(null);
    setShowScrollPrompt(false);
    setHasScrolledDown(false);

    // Find blog by slug
    const foundBlog = blogData.find(b => b.slug === slug);

    if (foundBlog) {
      setBlog(foundBlog);

      // Find related blogs
      const related = blogData
        .filter(b => {
          // Exclude current blog
          if (b.slug === slug) return false;

          // Priority 1: Same category
          if (b.category === foundBlog.category) return true;

          // Priority 2: Shared tags
          if (foundBlog.tags && b.tags) {
            const sharedTags = foundBlog.tags.filter(tag => b.tags.includes(tag));
            return sharedTags.length > 0;
          }

          return false;
        })
        .slice(0, 3); // Limit to 3 related blogs

      setRelatedBlogs(related);

      // Load comments from localStorage
      const savedComments = localStorage.getItem(`comments_${slug}`);
      if (savedComments) {
        try {
          setComments(JSON.parse(savedComments));
        } catch (error) {
          setComments([]);
        }
      } else {
        setComments([]);
      }
    } else {
      // Blog not found, redirect to blog list
      navigate('/blog');
      return;
    }

    setLoading(false);
    
    // Highlight continue button on page load
    setHighlightContinue(true);
    setTimeout(() => {
      setHighlightContinue(false);
    }, 3000); // Highlight for 3 seconds
  }, [slug, navigate]);

  // Timer countdown effect
  useEffect(() => {
    let interval = null;
    if (isTimerRunning && timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            setIsTimerRunning(false);
            setShowScrollPrompt(true);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    } else if (timer === 0 && isTimerRunning) {
      setIsTimerRunning(false);
      setShowScrollPrompt(true);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerRunning, timer]);

  // Scroll detection effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY || document.documentElement.scrollTop;
      // If user has scrolled down more than 300px
      if (scrollPosition > 300 && showScrollPrompt && !hasScrolledDown) {
        setHasScrolledDown(true);
        setShowScrollPrompt(false);
        setShowVerification(true);
        setHighlightVerification(true);
        // Highlight for 3 seconds
        setTimeout(() => {
          setHighlightVerification(false);
        }, 3000);
      }
    };

    if (showScrollPrompt && !hasScrolledDown) {
      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [showScrollPrompt, hasScrolledDown]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCommentDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleCommentSubmit = (e) => {
    e.preventDefault();

    if (!newComment.name.trim() || !newComment.email.trim() || !newComment.comment.trim()) {
      alert('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    // Create new comment
    const comment = {
      id: Date.now().toString(),
      name: newComment.name.trim(),
      email: newComment.email.trim(),
      comment: newComment.comment.trim(),
      date: new Date().toISOString(),
      replies: []
    };

    // Add comment to state
    const updatedComments = [...comments, comment];
    setComments(updatedComments);

    // Save to localStorage
    localStorage.setItem(`comments_${slug}`, JSON.stringify(updatedComments));

    // Reset form
    setNewComment({
      name: '',
      email: '',
      comment: ''
    });

    setIsSubmitting(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewComment(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Generate or get FAQ data based on blog
  const getFAQData = (blog) => {
    // If blog has FAQ data, use it
    if (blog.faq && Array.isArray(blog.faq) && blog.faq.length > 0) {
      return blog.faq;
    }

    // Otherwise, generate FAQ based on category
    const categoryFAQs = {
      'Financial Planning': [
        {
          question: 'How much should I save for an emergency fund?',
          answer: 'Financial experts recommend saving 3-6 months of your living expenses in an emergency fund. This should cover essential expenses like housing, food, utilities, and insurance. Start by setting a small monthly savings goal and gradually build it up over time.'
        },
        {
          question: 'When should I start financial planning?',
          answer: 'The best time to start financial planning is now, regardless of your age or income level. Starting early allows you to take advantage of compound interest and gives you more time to achieve your financial goals. Even small steps taken today can make a significant difference in the long run.'
        },
        {
          question: 'How do I prioritize my financial goals?',
          answer: 'Prioritize your financial goals based on urgency and importance. Start with building an emergency fund, then focus on paying off high-interest debt. After that, you can work on saving for retirement, major purchases, and other long-term goals. Review and adjust your priorities regularly as your situation changes.'
        }
      ],
      'Budgeting': [
        {
          question: 'What is the 50/30/20 rule?',
          answer: 'The 50/30/20 rule is a simple budgeting method that divides your after-tax income into three categories: 50% for needs (housing, food, utilities), 30% for wants (entertainment, dining out), and 20% for savings and debt repayment. This approach helps maintain balance while ensuring financial stability.'
        },
        {
          question: 'How often should I review my budget?',
          answer: 'You should review your budget at least monthly to track your spending and make adjustments. Regular reviews help you stay accountable, identify spending patterns, and ensure your budget remains effective. Consider doing a more detailed review quarterly to assess your progress toward financial goals.'
        },
        {
          question: 'What if I overspend in one category?',
          answer: 'If you overspend in one category, you can either reduce spending in another category to compensate, or adjust your budget for the next month. The key is to be flexible while maintaining your overall financial goals. Track your spending carefully to identify areas where you consistently overspend.'
        }
      ],
      'Investing': [
        {
          question: 'How much should I invest each month?',
          answer: 'Aim to invest at least 15-20% of your income, but start with whatever amount you can afford. Even small regular investments can grow significantly over time due to compound interest. Increase your investment amount as your income grows and you pay off debts.'
        },
        {
          question: 'What is the difference between stocks and mutual funds?',
          answer: 'Stocks represent ownership in individual companies, while mutual funds are collections of stocks, bonds, or other securities managed by professionals. Mutual funds offer diversification and professional management, making them a good option for beginners. Stocks offer more control but require more research and carry higher risk.'
        },
        {
          question: 'How long should I hold my investments?',
          answer: 'Investing is typically a long-term strategy. Most financial experts recommend holding investments for at least 5-10 years to ride out market fluctuations and benefit from compound growth. However, the exact timeframe depends on your financial goals, risk tolerance, and investment strategy.'
        }
      ],
      'Tax Planning': [
        {
          question: 'What are the best tax-saving investments under Section 80C?',
          answer: 'Popular Section 80C investments include ELSS mutual funds, PPF (Public Provident Fund), NSC (National Savings Certificate), tax-saving fixed deposits, and life insurance premiums. Each has different lock-in periods and returns, so choose based on your financial goals and risk tolerance.'
        },
        {
          question: 'How much can I save in taxes with proper planning?',
          answer: 'With proper tax planning, you can save significantly on your tax liability. Under Section 80C alone, you can claim deductions up to ₹1.5 lakh. Combined with other sections like 80D (health insurance), 80G (donations), and home loan benefits, you can optimize your tax savings effectively.'
        },
        {
          question: 'When is the best time to start tax planning?',
          answer: 'The best time to start tax planning is at the beginning of the financial year (April). This gives you the entire year to make tax-saving investments and take advantage of various deductions. However, you can still make some investments until March 31st of the financial year.'
        }
      ],
      'Credit': [
        {
          question: 'What is a good credit score?',
          answer: 'A credit score above 750 is generally considered good in India. Scores between 700-749 are fair, while scores above 750 are excellent and help you get better interest rates and loan approvals. Scores below 700 may limit your credit options.'
        },
        {
          question: 'How can I improve my credit score quickly?',
          answer: 'To improve your credit score quickly, pay all bills on time, reduce your credit utilization ratio below 30%, avoid applying for multiple credit cards or loans at once, and regularly check your credit report for errors. Maintaining a good mix of credit types also helps.'
        },
        {
          question: 'How often is my credit score updated?',
          answer: 'Credit scores are typically updated monthly when lenders report your payment activity to credit bureaus. However, it may take 30-45 days for changes to reflect in your score. Check your credit report regularly to monitor your progress and ensure accuracy.'
        }
      ]
    };

    return categoryFAQs[blog.category] || [
      {
        question: 'What are the key takeaways from this article?',
        answer: 'This article provides valuable insights and practical tips related to financial management. Review the main sections and implement the strategies that best fit your financial situation and goals.'
      },
      {
        question: 'How can I apply these concepts to my situation?',
        answer: 'Start by assessing your current financial situation, then identify which strategies from this article are most relevant to your goals. Create an action plan with specific steps and timelines, and review your progress regularly.'
      }
    ];
  };

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const handleContinueClick = () => {
    setTimer(5); // Start with 5 seconds
    setIsTimerRunning(true);
    setHighlightContinue(false);
  };

  const handleVerificationClick = () => {
    setIsLoading(true);
    
    // Show loader for 5 seconds, then navigate
    setTimeout(() => {
      // Get all blogs except the current one
      const otherBlogs = blogData.filter(b => b.slug !== slug);
      
      if (otherBlogs.length > 0) {
        // Select a random blog
        const randomBlog = otherBlogs[Math.floor(Math.random() * otherBlogs.length)];
        navigate(`/blog/${randomBlog.slug}`);
      } else {
        // If no other blogs, go to blog list
        navigate('/blog');
      }
    }, 5000); // 5 seconds
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (!blog) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {blog && (
        <SEO
          title={blog.title}
          description={blog.excerpt}
          keywords={blog.keywords || (blog.tags ? blog.tags.join(', ') : 'finance, financial planning, investment')}
          type="article"
          author={blog.author}
          publishedTime={blog.date}
          articleSection={blog.category}
          tags={blog.tags || []}
          image={blog.image}
          breadcrumbs={[
            { name: 'Home', url: '/' },
            { name: 'Blog', url: '/blog' },
            { name: blog.title, url: `/blog/${blog.slug}` }
          ]}
        />
      )}
      {blog && (
        <Breadcrumb
          items={[
            { name: 'Home', url: '/' },
            { name: 'Blog', url: '/blog' },
            { name: blog.title, url: `/blog/${blog.slug}` }
          ]}
        />
      )}

      <div className="text-center">
        {!isTimerRunning && !showScrollPrompt && !showVerification && (
          <button 
            onClick={handleContinueClick}
            className={`bg-red-500 animate-pulse ring-4 ring-red-300 ring-opacity-75 py-2 px-2 mx-auto block mt-12 text-white  min-w-[100px]  rounded-full text-sm font-medium transition-all duration-300 ${
              highlightContinue 
                ? 'animate-pulse ring-4 ring-red-300 ring-opacity-75 shadow-2xl scale-110 transform' 
                : 'hover:bg-red-600 hover:shadow-lg hover:scale-105'
            }`}
          >
            continue
          </button>
        )}
        {isTimerRunning && timer > 0 && (
          <div className="mt-12">
            <div className="text-center">
              <div className="inline-block bg-red-500 text-white  min-w-[100px] py-2 px-2 rounded-full text-lg font-bold">
                {timer}
              </div>
            </div>
          </div>
        )}
        {showScrollPrompt && !hasScrolledDown && (
          <div className="mt-12 text-center animate-bounce">
            <p className="text-gray-700 font-semibold text-lg mb-4">Please scroll down and click the verification button to verify  </p>
            <div className="flex flex-col items-center">
              <svg className="w-8 h-8 text-red-500 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          </div>
        )}
      </div>
      {/* Blog Header */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Category Badge */}
        <div className="mb-6">
          <span className="bg-primary-600 text-white px-4 py-2 rounded-full text-sm font-medium">
            {blog.category}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
          {blog.title}
        </h1>

        {/* Meta Information */}
        <div className="flex flex-wrap items-center gap-4 mb-8 pb-8 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-primary-600 font-semibold text-lg">
                {blog.author.charAt(0)}
              </span>
            </div>
            <div>
              <p className="font-semibold text-gray-900">{blog.author}</p>
              <p className="text-sm text-gray-500">{formatDate(blog.date)}</p>
            </div>
          </div>

          <div className="flex items-center text-gray-500 text-sm ml-auto">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {blog.readTime}
          </div>
        </div>

        {/* Featured Image */}
        <div className="mb-8 rounded-xl overflow-hidden shadow-lg">
          <img
            src={blog.image}
            alt={blog.title}
            className="w-full h-auto object-cover"
            loading="lazy"
            decoding="async"
            width="1200"
            height="600"
          // onError={(e) => {
          //   e.target.src = 'https://via.placeholder.com/1200x600?text=Blog+Image';
          // }}
          />
        </div>

        {/* Blog Content */}
        <div
          className="mb-12 blog-content"
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />

        {/* Tags */}
        {blog.tags && blog.tags.length > 0 && (
          <div className="mb-12 pb-8 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {blog.tags.map((tag, index) => (
                <span
                  key={index}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <div className="mb-12 pb-8 border-b border-gray-200">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {getFAQData(blog).map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset"
                  aria-expanded={openFAQ === index}
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-4 flex-1">
                    {faq.question}
                  </h3>
                  <svg
                    className={`w-6 h-6 text-primary-600 flex-shrink-0 transition-transform duration-200 ${openFAQ === index ? 'transform rotate-180' : ''
                      }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${openFAQ === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                >
                  <div className="px-6 pb-5 pt-0">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Related Blogs Section */}
        {relatedBlogs.length > 0 && (
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedBlogs.map((relatedBlog) => (
                <Link
                  key={relatedBlog.id}
                  to={`/blog/${relatedBlog.slug}`}
                  className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-gray-100"
                >
                  {/* Blog Image */}
                  <div className="relative h-40 overflow-hidden bg-gray-200">
                    <img
                      src={relatedBlog.image}
                      alt={relatedBlog.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      loading="lazy"
                      decoding="async"
                      width="400"
                      height="160"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                        {relatedBlog.category}
                      </span>
                    </div>
                  </div>

                  {/* Blog Content */}
                  <div className="p-5">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {relatedBlog.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {relatedBlog.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{formatDate(relatedBlog.date)}</span>
                      <span>{relatedBlog.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Back to Blog */}
        <div className="text-center">
          <Link
            to="/blog"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 font-semibold text-lg transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            View All Blog Posts
          </Link>
        </div>

        {/* Comments Section */}
        <div className="mb-12 pb-8 mt-12 border-b border-gray-200">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Comments ({comments.length})</h2>

          {/* Comment Form */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Leave a Comment</h3>
            <form onSubmit={handleCommentSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={newComment.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={newComment.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
                  Comment *
                </label>
                <textarea
                  id="comment"
                  name="comment"
                  value={newComment.comment}
                  onChange={handleInputChange}
                  rows="5"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all resize-none"
                  placeholder="Share your thoughts..."
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Posting...' : 'Post Comment'}
              </button>
            </form>
          </div>

          {/* Comments List */}
          {comments.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="text-gray-600">No comments yet. Be the first to share your thoughts!</p>
            </div>
          ) : (
            <div className="space-y-6">
              {comments.map((comment) => (
                <div key={comment.id} className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-primary-600 font-semibold text-lg">
                        {comment.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900">{comment.name}</h4>
                          <p className="text-sm text-gray-500">{formatCommentDate(comment.date)}</p>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{comment.comment}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {showVerification && (
          <div>
            {isLoading ? (
              <div className="text-center mt-12">
                <div className="inline-block">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
                  <p className="text-gray-600 font-medium">Loading...</p>
                </div>
              </div>
            ) : (
              <button 
                onClick={handleVerificationClick}
                className={`bg-red-500 animate-pulse ring-4 ring-red-300 ring-opacity-75 py-2 px-2 mx-auto block mt-12 text-white  min-w-[100px]  rounded-full text-sm font-medium transition-all duration-300 ${
                  highlightVerification 
                    ? 'animate-pulse ring-4 ring-red-300 ring-opacity-75 shadow-2xl scale-110 transform' 
                    : 'hover:bg-red-600 hover:shadow-lg hover:scale-105'
                }`}
              >
                verification
              </button>
            )}
          </div>
        )}
      </article>
    </div>
  );
};

export default BlogDetails;

